import type { IBimRelationshipEdge, IBimRelationshipNode } from '@skystone/shared';

/**
 * Derives the highlight/dim state for the BIM graph from the current selection.
 *
 * Selection never hides tables or relationships — everything stays on screen;
 * unrelated items are returned in the `dimmed*` sets so the view can lower their
 * opacity. `highlightedColumnsByNode` marks which key field on each card takes
 * part in the active selection.
 */

export interface ISelectionHighlight {
  dimmedNodeIds: Set<string>;
  dimmedEdgeIds: Set<string>;
  highlightedColumnsByNode: Map<string, Set<string>>;
}

function emptyHighlight(): ISelectionHighlight {
  return {
    dimmedNodeIds: new Set<string>(),
    dimmedEdgeIds: new Set<string>(),
    highlightedColumnsByNode: new Map<string, Set<string>>(),
  };
}

function addColumn(map: Map<string, Set<string>>, nodeId: string, column: string): void {
  const set = map.get(nodeId) ?? new Set<string>();
  set.add(column);
  map.set(nodeId, set);
}

function dimComplement(
  allIds: string[],
  relatedIds: Set<string>,
): Set<string> {
  const dimmed = new Set<string>();
  for (const id of allIds) {
    if (!relatedIds.has(id)) {
      dimmed.add(id);
    }
  }
  return dimmed;
}

function highlightForEdge(
  nodes: IBimRelationshipNode[],
  edges: IBimRelationshipEdge[],
  edge: IBimRelationshipEdge,
): ISelectionHighlight {
  const relatedNodeIds = new Set<string>([edge.fromTable, edge.toTable]);
  const relatedEdgeIds = new Set<string>([edge.id]);
  const highlightedColumnsByNode = new Map<string, Set<string>>();
  addColumn(highlightedColumnsByNode, edge.fromTable, edge.fromColumn);
  addColumn(highlightedColumnsByNode, edge.toTable, edge.toColumn);

  return {
    dimmedNodeIds: dimComplement(nodes.map((node) => node.id), relatedNodeIds),
    dimmedEdgeIds: dimComplement(edges.map((item) => item.id), relatedEdgeIds),
    highlightedColumnsByNode,
  };
}

function highlightForNode(
  nodes: IBimRelationshipNode[],
  edges: IBimRelationshipEdge[],
  nodeId: string,
): ISelectionHighlight {
  const relatedNodeIds = new Set<string>([nodeId]);
  const relatedEdgeIds = new Set<string>();
  const highlightedColumnsByNode = new Map<string, Set<string>>();

  for (const edge of edges) {
    if (edge.fromTable !== nodeId && edge.toTable !== nodeId) {
      continue;
    }

    relatedEdgeIds.add(edge.id);
    relatedNodeIds.add(edge.fromTable);
    relatedNodeIds.add(edge.toTable);
    addColumn(highlightedColumnsByNode, edge.fromTable, edge.fromColumn);
    addColumn(highlightedColumnsByNode, edge.toTable, edge.toColumn);
  }

  return {
    dimmedNodeIds: dimComplement(nodes.map((node) => node.id), relatedNodeIds),
    dimmedEdgeIds: dimComplement(edges.map((item) => item.id), relatedEdgeIds),
    highlightedColumnsByNode,
  };
}

export function computeHighlight(
  nodes: IBimRelationshipNode[],
  edges: IBimRelationshipEdge[],
  selectedNodeId: string | null,
  selectedEdgeId: string | null,
): ISelectionHighlight {
  if (selectedEdgeId) {
    const edge = edges.find((item) => item.id === selectedEdgeId);
    if (edge) {
      return highlightForEdge(nodes, edges, edge);
    }
  }

  if (selectedNodeId && nodes.some((node) => node.id === selectedNodeId)) {
    return highlightForNode(nodes, edges, selectedNodeId);
  }

  return emptyHighlight();
}
